/**
 * Turns react-query status into what a Brugerstyring table should show.
 *
 * The sort direction and page number are part of every query key, so changing
 * either starts a brand-new query. Without `placeholderData: keepPreviousData`
 * that means `isPending`, which blanked the whole table into skeletons on every
 * sort click (LAR-410). With it, the previous rows stay mounted and the query
 * reports `isPlaceholderData` until the new ones arrive.
 *
 * Skeletons are therefore only right when there is genuinely nothing to show.
 * When rows are on screen but belong to the previous sort, the table says so
 * with a busy affordance instead of throwing the content away.
 */

export interface QueryStatusFlags {
  /** Nothing has loaded for this query yet, placeholder included. */
  isPending: boolean;
  /** What's on screen came from the previous query key. */
  isPlaceholderData: boolean;
}

export interface TableLoadingState {
  /** Render placeholder rows — there is nothing worth keeping on screen. */
  showSkeleton: boolean;
  /** Rows are real but belong to the previous sort or page. */
  isRefreshing: boolean;
}

/**
 * Combines every query a table depends on. A table is only ready once all of
 * them are, and is refreshing if any of them is serving previous-key data.
 */
export const tableLoadingState = (
  ...queries: readonly QueryStatusFlags[]
): TableLoadingState => {
  const showSkeleton = queries.some((query) => query.isPending);

  return {
    showSkeleton,
    isRefreshing:
      !showSkeleton && queries.some((query) => query.isPlaceholderData),
  };
};

/**
 * Applied to the table body while `isRefreshing`. The rows are real, just one
 * sort behind, so they stay readable rather than being replaced — dimmed to
 * show they are not final, and inert so a click cannot land on a row that is
 * about to be replaced underneath the cursor.
 */
export const REFRESHING_ROWS_CLASS =
  "opacity-50 pointer-events-none transition-opacity duration-150";
